import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { getDurationHours, getInsights, getWarningsForLinqset, getWarningText, isSupplyChain, Linqset, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, NODE_WARNING } from '../common/linqset-model';
import { MaterialModule } from '../material/material.module';
import { REAL_SECONDS_PER_TICK, TickStatistics, TemporalService, MINUTES_IN_DAY } from './temporal.service';
import { AdjacencyTypes, GraphComponent, INode } from '@yfiles/yfiles';
import { GraphProcessingService } from '../model-services/graph-processing.service';
import { GraphService } from '../canvas/graph.service';

@Component({
    selector: 'app-temporal-settings',
    styleUrls: ['./temporal-settings.css'],
    templateUrl: 'temporal-settings.html',
    standalone: true,
    imports: [MaterialModule, FormsModule]
})
export class TemporalSettingsComponent implements OnDestroy, OnInit, OnChanges {
    @Input() linqset:Linqset
    @Input() organisationLinqset:Linqset
    @Input() graphComponent:GraphComponent

    Math = Math
    
    errorMessages:{node:LinqsetNode, message:string}[] = []

    minutesPerSecond:number = 1         // how many model minutes are processed each real second
    statisticsSubscription:Subscription
    editModelSubscription:Subscription
    inputs:{node:INode, itemsPerYear:number, name:string}[]
    roles:any[]
    rolesMap:{}

    maxSlider = 60          // maxium value can set slider. adjusted depending on minimum action duration

    statistics: TickStatistics

    @Output() closeEvent:EventEmitter<string> = new EventEmitter<string>()
    @Output() selectNodeEvent:EventEmitter<string> = new EventEmitter<string>()
       
    constructor(public animationService:TemporalService, private graphProcessingService:GraphProcessingService, private graphService:GraphService) {
    }

    ngOnChanges(changes: SimpleChanges): void {   
        if (this.linqset && this.graphComponent)     
            this.populateForm()
    }

    ngOnDestroy(): void {
        if (this.statisticsSubscription) this.statisticsSubscription.unsubscribe()
        if (this.editModelSubscription) this.editModelSubscription.unsubscribe()
        // leaving the panel (or the page) stops the run and puts the model's own inputs back
        this.animationService.clear()
    }

    ngOnInit(): void {

        if (!this.statisticsSubscription) {
            this.statisticsSubscription = this.animationService.statisticsEvent.subscribe((e:TickStatistics) => {
                this.statistics = e
            })
        }

        if (!this.editModelSubscription) {
            this.editModelSubscription = this.graphProcessingService.processCompletedEvent.subscribe(async e => {
                if (!this.animationService.isRunning) this.populateForm()
            })
        }

        this.populateForm()
    }

    populateForm() {
        this.graphService.removeHeatmap(this.graphComponent)

        // keep numbers the user has changed, where the input or role is still there
        const previousInputs = new Map((this.inputs || []).map(i => [i.node.tag?.node?._key, i.itemsPerYear]))
        const previousRoles = new Map((this.roles || []).filter(r => r.number !== r.original).map(r => [r._key, r.number]))

        this.roles = []
        this.inputs = []

        this.maxSlider = 60
        if (!this.graphComponent) return

        this.graphComponent.graph.nodes.filter(iNode => iNode.tag?.node && (iNode.tag?.node.superType === NODE_SUPERTYPE.ROLE || isSupplyChain(iNode.tag?.node.superType))).forEach(iNode => {
            const n = iNode.tag.node
            const i = getInsights(n, this.linqset)
            if (!i) return
            if (n.superType === NODE_SUPERTYPE.ROLE && n.type !== NODE_TYPE.EXTERNAL_ORGANISATION && this.roles.findIndex(r => r._key === n._key) < 0) {
                const o = this.organisationLinqset && getInsights(n, this.organisationLinqset)
                const people = i.numPeople || 0
                const actionKeys = this.graphComponent.graph.edgesAt(iNode, AdjacencyTypes.OUTGOING).filter(e => e.targetNode.tag?.node).map(e => e.targetNode.tag.node._key)
                // capacity per person; a role without people has none to share out
                const r = {_key:n._key, node:iNode, name:n.name, original:people, number:previousRoles.get(n._key) ?? people,
                    capacity:people ? (i.modelledCapacity||0)/people : 0, orgCapacity:people && o ? (o.modelledCapacity||0)/people : 0, actionKeys}
                this.roles.push(r)
            }
            else if (i.isInput) {
                this.inputs.push({node:iNode, itemsPerYear:previousInputs.get(n._key) ?? i.numberProcessed, name:n.name})
            }
            if (n.superType === NODE_SUPERTYPE.ACTION) {
                const d = getDurationHours(n.properties.duration)
                if (d > 0) {
                    const minutes = d*60/REAL_SECONDS_PER_TICK

                    if (minutes < this.maxSlider) this.maxSlider = (this.Math.round(minutes)||1)
                }
            }
        })
        
        // the speed can't be faster than the shortest action
        if (this.minutesPerSecond > this.maxSlider) this.minutesPerSecond = this.maxSlider

        const warnings = getWarningsForLinqset(this.linqset)

        this.errorMessages = warnings.filter(lw => lw.warnings.find(w => w === NODE_WARNING.MISSING_DURATION || w === NODE_WARNING.MISSING_CAPACITY || w === NODE_WARNING.MISSING_FREQUENCY)).map(lw => {
            return {node: lw.node, message: lw.warnings.map(w => getWarningText(w)).join(", ")}
        })
    }

    toggleModel() {
        if (this.animationService.isRunning) this.animationService.stopAnimation() 
        else {
            this.rolesMap = {}
            this.inputs.forEach(i => i.itemsPerYear = Math.max(0, Math.round(i.itemsPerYear || 0)))
            this.roles.forEach(r => r.number = Math.max(0, Math.round(r.number || 0)))
            this.roles.forEach(r => {
                if (r.original !== r.number) {
                    this.rolesMap[r._key] = {number: r.number, capacity: r.capacity}
                }
            })
            this.animationService.runAnimation(this.graphComponent, this.minutesPerSecond, this.linqset, this.inputs, this.rolesMap)
        }
    }

    showTime(minutes:number) {
        const min = Math.trunc(minutes)
        if (min > MINUTES_IN_DAY) {
            const days = Math.trunc(min/MINUTES_IN_DAY)
            const remainingMinutes = min - days*MINUTES_IN_DAY
            return `${days} days ${remainingMinutes} mins`
        }else {
            const secs = Math.round((minutes-min)*60)
            return min===0?`${secs} seconds`:`${min} mins ${secs} seconds`
        }
    }

    getElapsedTime() {
        return this.showTime(this.statistics.tick*REAL_SECONDS_PER_TICK/60)
    }
    
    getModelMinutes(ticks:number) {
        return this.showTime(ticks*REAL_SECONDS_PER_TICK*this.minutesPerSecond)
    }

    clearResults() {
        this.animationService.clear()
    }

    selectNode(k:string) {
        this.selectNodeEvent.emit(k)
    }

    closePanel() {
        this.animationService.clear()
        this.closeEvent.emit()
    }
}
